import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  EMPTY_OAUTH_FLOW_STATE,
  type OAuthFlowState,
  type OAuthFlowStep,
} from "@mcpjam/sdk/browser";
import { createInspectorOAuthStateMachine } from "@/lib/oauth/debug-state-machine-adapter";
import { OAuthSequenceDiagram } from "@/components/oauth/OAuthSequenceDiagram";
import { OAuthAuthorizationModal } from "@/components/oauth/OAuthAuthorizationModal";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "./ui/resizable";
import { track } from "@/lib/analytics";
import { OAuthProfileModal } from "./oauth/OAuthProfileModal";
import { type OAuthTestProfile } from "@/lib/oauth/profile";
import { OAuthFlowLogger } from "./oauth/OAuthFlowLogger";
import type { ServerFormData } from "@/shared/types.js";
import type { ServerWithName } from "@/hooks/use-app-state";
import { deriveOAuthProfileFromServer } from "./oauth/utils";
import { RefreshTokensConfirmModal } from "./oauth/RefreshTokensConfirmModal";

export interface OAuthTokensFromFlow {
  accessToken: string;
  refreshToken?: string;
  tokenType?: string;
  expiresIn?: number;
  clientId?: string;
  clientSecret?: string;
  // AS URL discovered during the debugger flow. Forwarded to the hosted
  // backend so it can refresh without re-discovering against a resource it
  // can't reach itself (e.g. localhost).
  authorizationServerUrl?: string;
}

declare global {
  interface Window {
    __oauthDebuggerE2EFlowState?: {
      authorizationUrl?: string;
      currentStep: OAuthFlowStep;
      state?: string;
    };
  }
}

const deriveServerIdentifier = (profile: OAuthTestProfile): string => {
  const trimmedUrl = profile.serverUrl.trim();
  if (!trimmedUrl) {
    return "oauth-flow-target";
  }

  try {
    const url = new URL(trimmedUrl);
    return url.host;
  } catch {
    return trimmedUrl;
  }
};

const buildHeaderMap = (
  headers: Array<{ key: string; value: string }>,
): Record<string, string> | undefined => {
  const entries = headers
    .map((header) => ({
      key: header.key.trim(),
      value: header.value.trim(),
    }))
    .filter((header) => header.key.length > 0);

  if (!entries.length) {
    return undefined;
  }

  return Object.fromEntries(entries.map(({ key, value }) => [key, value]));
};

const describeRegistrationStrategy = (strategy: string): string => {
  if (strategy === "cimd") return "CIMD (URL-based)";
  if (strategy === "dcr") return "Dynamic (DCR)";
  return "Pre-registered";
};

const isHttpServer = (server?: ServerWithName) =>
  Boolean(server && "url" in server.config);

interface OAuthFlowTabProps {
  serverConfigs: Record<string, ServerWithName>;
  selectedServerName: string;
  hasHeaderServers?: boolean;
  areServersHydrated?: boolean;
  onSelectServer: (serverName: string) => void;
  // Resolves false when the save failed (the hook toasts the reason), so
  // callers can keep the modal open instead of treating every call as saved.
  onSaveServerConfig?: (
    formData: ServerFormData,
    options?: { oauthProfile?: OAuthTestProfile; originalServerName?: string },
  ) => void | boolean | Promise<void | boolean>;
  onConnectWithTokens?: (
    serverName: string,
    tokens: OAuthTokensFromFlow,
    serverUrl: string,
  ) => Promise<void>;
  onRefreshTokens?: (
    serverName: string,
    tokens: OAuthTokensFromFlow,
    serverUrl: string,
  ) => Promise<void>;
  /**
   * Bumped by the shell when the header "Add Server" button is clicked while
   * this tab is active, so the Configure-OAuth-Target modal opens instead of
   * the generic Add Server modal. Each new value (not the initial one) opens it.
   */
  openProfileModalSignal?: number;
}

export const OAuthFlowTab = ({
  serverConfigs,
  selectedServerName,
  hasHeaderServers = false,
  areServersHydrated = true,
  onSelectServer,
  onSaveServerConfig,
  onConnectWithTokens,
  onRefreshTokens,
  openProfileModalSignal,
}: OAuthFlowTabProps) => {
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  // "edit" opens the modal prefilled with the selected server; "add" opens it
  // blank so a new target can be saved without touching the current one.
  const [profileModalMode, setProfileModalMode] = useState<"edit" | "add">(
    "edit",
  );
  const openProfileModal = useCallback((mode: "edit" | "add") => {
    setProfileModalMode(mode);
    setIsProfileModalOpen(true);
  }, []);

  // Open the modal when the shell bumps the signal (header "Add Server"). Skip
  // the initial value so it doesn't pop open on mount.
  const prevOpenSignalRef = useRef(openProfileModalSignal);
  useEffect(() => {
    if (openProfileModalSignal === prevOpenSignalRef.current) return;
    prevOpenSignalRef.current = openProfileModalSignal;
    // The header button reads "Add Server" — open blank, not prefilled with
    // the current selection.
    openProfileModal("add");
  }, [openProfileModalSignal, openProfileModal]);
  const [pendingServerSelection, setPendingServerSelection] = useState<
    string | null
  >(null);
  const [oauthFlowState, setOAuthFlowState] = useState<OAuthFlowState>(
    EMPTY_OAUTH_FLOW_STATE,
  );
  const [focusedStep, setFocusedStep] = useState<OAuthFlowStep | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isRefreshTokensModalOpen, setIsRefreshTokensModalOpen] =
    useState(false);
  const [isApplyingTokens, setIsApplyingTokens] = useState(false);

  const httpServerCount = useMemo(
    () =>
      Object.values(serverConfigs).filter((server) => isHttpServer(server))
        .length,
    [serverConfigs],
  );

  const selectedServer =
    selectedServerName !== "none"
      ? serverConfigs[selectedServerName]
      : undefined;
  const activeServer = isHttpServer(selectedServer)
    ? selectedServer
    : undefined;

  useEffect(() => {
    if (
      pendingServerSelection &&
      serverConfigs[pendingServerSelection] &&
      isHttpServer(serverConfigs[pendingServerSelection])
    ) {
      onSelectServer(pendingServerSelection);
      setPendingServerSelection(null);
    }
  }, [pendingServerSelection, serverConfigs, onSelectServer]);

  useEffect(() => {
    // On a hard reload, the project server query resolves after this tab first
    // mounts. Do not mistake that loading gap for an empty project. If an
    // eligible server then appears in the header, also close any dialog opened
    // by the earlier empty state.
    if (!areServersHydrated) return;
    if (hasHeaderServers) {
      setIsProfileModalOpen(false);
      return;
    }
    if (httpServerCount === 0) {
      setIsProfileModalOpen(true);
    }
  }, [areServersHydrated, hasHeaderServers, httpServerCount]);

  const profile = useMemo(
    () => deriveOAuthProfileFromServer(activeServer),
    [activeServer],
  );

  const hasProfile = Boolean(activeServer && profile.serverUrl.trim());
  const serverIdentifier = useMemo(
    () => (activeServer ? activeServer.name : deriveServerIdentifier(profile)),
    [activeServer, profile.serverUrl],
  );

  const protocolVersion = profile.protocolVersion;
  const registrationStrategy = profile.registrationStrategy;

  const oauthFlowStateRef = useRef(oauthFlowState);
  useEffect(() => {
    oauthFlowStateRef.current = oauthFlowState;
  }, [oauthFlowState]);

  useEffect(() => {
    if (
      !import.meta.env.DEV ||
      !window.location.pathname.startsWith("/__e2e/oauth-debugger")
    ) {
      return;
    }

    window.__oauthDebuggerE2EFlowState = {
      authorizationUrl: oauthFlowState.authorizationUrl,
      currentStep: oauthFlowState.currentStep,
      state: oauthFlowState.state,
    };
  }, [
    oauthFlowState.authorizationUrl,
    oauthFlowState.currentStep,
    oauthFlowState.state,
  ]);

  useEffect(() => {
    setFocusedStep(null);
  }, [oauthFlowState.currentStep]);

  const updateOAuthFlowState = useCallback(
    (updates: Partial<OAuthFlowState>) => {
      setOAuthFlowState((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  const processedCodeRef = useRef<string | null>(null);
  const exchangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset OAuth flow state when switching servers
  const prevServerNameRef = useRef(selectedServerName);
  useEffect(() => {
    if (prevServerNameRef.current !== selectedServerName) {
      prevServerNameRef.current = selectedServerName;
      setOAuthFlowState({
        ...EMPTY_OAUTH_FLOW_STATE,
        serverUrl: profile.serverUrl || undefined,
      });
      processedCodeRef.current = null;
      if (exchangeTimeoutRef.current) {
        clearTimeout(exchangeTimeoutRef.current);
        exchangeTimeoutRef.current = null;
      }
    }
  }, [selectedServerName, profile.serverUrl]);

  const resetOAuthFlow = useCallback(
    (serverUrlOverride?: string) => {
      const nextServerUrl = serverUrlOverride ?? profile.serverUrl;
      setOAuthFlowState({
        ...EMPTY_OAUTH_FLOW_STATE,
        serverUrl: nextServerUrl || undefined,
      });
      processedCodeRef.current = null;
      if (exchangeTimeoutRef.current) {
        clearTimeout(exchangeTimeoutRef.current);
        exchangeTimeoutRef.current = null;
      }
    },
    [profile.serverUrl],
  );

  const clearInfoLogs = () => {
    updateOAuthFlowState({ infoLogs: [] });
  };

  const clearHttpHistory = () => {
    updateOAuthFlowState({ httpHistory: [] });
  };

  const customHeaders = useMemo(
    () => buildHeaderMap(profile.customHeaders),
    [profile.customHeaders],
  );

  const oauthStateMachine = useMemo(() => {
    if (!hasProfile) return null;

    return createInspectorOAuthStateMachine({
      protocolVersion,
      state: oauthFlowStateRef.current,
      getState: () => oauthFlowStateRef.current,
      updateState: updateOAuthFlowState,
      serverUrl: profile.serverUrl,
      serverName: serverIdentifier,
      customScopes: profile.scopes.trim() || undefined,
      customHeaders,
      registrationStrategy,
      preregisteredClientId: profile.clientId.trim() || undefined,
      // Preserve the exact typed secret — trimming would silently change a
      // secret that legitimately has leading/trailing whitespace before it
      // ever reaches the live OAuth token exchange.
      preregisteredClientSecret: profile.clientSecret.trim()
        ? profile.clientSecret
        : undefined,
      hasClientSecret: Boolean(activeServer?.hasClientSecret),
    });
  }, [
    hasProfile,
    protocolVersion,
    profile.serverUrl,
    profile.scopes,
    profile.clientId,
    profile.clientSecret,
    serverIdentifier,
    customHeaders,
    registrationStrategy,
    activeServer?.hasClientSecret,
    updateOAuthFlowState,
  ]);

  const proceedToNextStep = useCallback(async () => {
    if (oauthStateMachine) {
      await oauthStateMachine.proceedToNextStep();
    }
  }, [oauthStateMachine]);

  const handleAdvance = useCallback(async () => {
    track("oauth_flow_tab_next_step_button_clicked", {
      location: "oauth_flow_tab",
      currentStep: oauthFlowState.currentStep,
      protocolVersion,
      registrationStrategy,
      hasProfile,
      targetUrlConfigured: Boolean(profile.serverUrl),
    });

    if (
      oauthFlowState.currentStep === "authorization_request" ||
      oauthFlowState.currentStep === "generate_pkce_parameters"
    ) {
      if (oauthFlowState.currentStep === "generate_pkce_parameters") {
        await proceedToNextStep();
      }
      setIsAuthModalOpen(true);
    } else {
      await proceedToNextStep();
    }
  }, [
    hasProfile,
    oauthFlowState.currentStep,
    proceedToNextStep,
    profile.serverUrl,
    protocolVersion,
    registrationStrategy,
  ]);

  const continueLabel = !hasProfile
    ? "Configure Target"
    : oauthFlowState.currentStep === "complete"
      ? "Flow Complete"
      : oauthFlowState.isInitiatingAuth
        ? "Continue"
        : oauthFlowState.currentStep === "authorization_request" ||
            oauthFlowState.currentStep === "generate_pkce_parameters"
          ? "Authorize"
          : "Continue";
  const continueDisabled =
    !hasProfile ||
    !oauthStateMachine ||
    oauthFlowState.isInitiatingAuth ||
    oauthFlowState.currentStep === "complete";

  // Determine if we can apply tokens (flow complete with access token)
  const isServerConnected = activeServer?.connectionStatus === "connected";
  const canApplyTokens =
    oauthFlowState.currentStep === "complete" &&
    oauthFlowState.accessToken &&
    activeServer;

  // Extract tokens from flow state
  const extractTokensFromFlowState = useCallback(
    (): OAuthTokensFromFlow => ({
      accessToken: oauthFlowState.accessToken!,
      refreshToken: oauthFlowState.refreshToken,
      tokenType: oauthFlowState.tokenType,
      expiresIn: oauthFlowState.expiresIn,
      clientId: oauthFlowState.clientId,
      clientSecret: oauthFlowState.clientSecret,
      authorizationServerUrl: oauthFlowState.authorizationServerUrl,
    }),
    [
      oauthFlowState.accessToken,
      oauthFlowState.refreshToken,
      oauthFlowState.tokenType,
      oauthFlowState.expiresIn,
      oauthFlowState.clientId,
      oauthFlowState.clientSecret,
      oauthFlowState.authorizationServerUrl,
    ],
  );

  // Handler for connecting server with new tokens
  const handleConnectServer = useCallback(async () => {
    if (!activeServer || !onConnectWithTokens) return;
    setIsApplyingTokens(true);
    try {
      await onConnectWithTokens(
        activeServer.name,
        extractTokensFromFlowState(),
        profile.serverUrl,
      );
    } finally {
      setIsApplyingTokens(false);
    }
  }, [
    activeServer,
    onConnectWithTokens,
    extractTokensFromFlowState,
    profile.serverUrl,
  ]);

  // Handler for refreshing tokens (called after modal confirmation)
  const handleRefreshTokensConfirm = useCallback(async () => {
    if (!activeServer || !onRefreshTokens) return;
    setIsApplyingTokens(true);
    try {
      await onRefreshTokens(
        activeServer.name,
        extractTokensFromFlowState(),
        profile.serverUrl,
      );
      setIsRefreshTokensModalOpen(false);
    } finally {
      setIsApplyingTokens(false);
    }
  }, [
    activeServer,
    onRefreshTokens,
    extractTokensFromFlowState,
    profile.serverUrl,
  ]);

  useEffect(() => {
    const processOAuthCallback = (
      code: string,
      state: string | undefined,
      iss?: string | undefined
    ) => {
      if (processedCodeRef.current === code) {
        return;
      }

      const expectedState = oauthFlowStateRef.current.state;
      const currentStep = oauthFlowStateRef.current.currentStep;
      const isWaitingForCode =
        currentStep === "received_authorization_code" ||
        currentStep === "authorization_request";

      if (!isWaitingForCode) {
        return;
      }

      if (!expectedState) {
        updateOAuthFlowState({
          error:
            "Flow was reset. Please start a new authorization by clicking 'Next Step'.",
        });
        return;
      }

      if (state !== expectedState) {
        updateOAuthFlowState({
          error:
            "Invalid state parameter - this authorization code is from a previous flow. Please try again.",
        });
        return;
      }

      processedCodeRef.current = code;

      if (exchangeTimeoutRef.current) {
        clearTimeout(exchangeTimeoutRef.current);
      }

      updateOAuthFlowState({
        authorizationCode: code,
        // 2R-iss / review F8: record the RFC 9207 `iss` so the machine's
        // authorization-response issuer step validates it (it reads
        // `state.authorizationResponseIss`) instead of leaving it unset.
        authorizationResponseIss: iss,
        error: undefined,
      });

      exchangeTimeoutRef.current = setTimeout(() => {
        oauthStateMachine?.proceedToNextStep();
        exchangeTimeoutRef.current = null;
      }, 500);
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type === "OAUTH_CALLBACK" && event.data?.code) {
        processOAuthCallback(event.data.code, event.data.state, event.data.iss);
      }
    };

    const handleElectronOAuthCallback = (event: Event) => {
      const callbackUrl = (event as CustomEvent<string>).detail;
      if (!callbackUrl) {
        return;
      }

      try {
        const parsed = new URL(callbackUrl);
        if (parsed.searchParams.get("flow") !== "debug") {
          return;
        }

        const error = parsed.searchParams.get("error");
        const errorDescription = parsed.searchParams.get("error_description");
        if (error) {
          if (exchangeTimeoutRef.current) {
            clearTimeout(exchangeTimeoutRef.current);
            exchangeTimeoutRef.current = null;
          }

          updateOAuthFlowState({
            error: errorDescription ?? error,
          });
          return;
        }

        const code = parsed.searchParams.get("code");
        const state = parsed.searchParams.get("state");
        const iss = parsed.searchParams.get("iss");
        if (code) {
          processOAuthCallback(code, state ?? undefined, iss ?? undefined);
        }
      } catch (error) {
        console.error("Failed to process Electron OAuth callback:", error);
      }
    };

    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel("oauth_callback_channel");
      channel.onmessage = (event) => {
        if (event.data?.type === "OAUTH_CALLBACK" && event.data?.code) {
          processOAuthCallback(
            event.data.code,
            event.data.state,
            event.data.iss
          );
        }
      };
    } catch (error) {
      // BroadcastChannel not supported; fallback to window message only
    }

    window.addEventListener("message", handleMessage);
    window.addEventListener(
      "electron-oauth-callback",
      handleElectronOAuthCallback as EventListener,
    );
    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener(
        "electron-oauth-callback",
        handleElectronOAuthCallback as EventListener,
      );
      channel?.close();
    };
  }, [oauthStateMachine, updateOAuthFlowState]);

  useEffect(() => {
    track("oauth_flow_tab_viewed", {
      location: "oauth_flow_tab",
    });
  }, []);

  const headerDescription = hasProfile
    ? profile.serverUrl
    : "Paste an MCP base URL to start debugging the OAuth flow.";

  return (
    <div className="h-full flex flex-col bg-background">
      <div className="flex-1 overflow-hidden">
        {hasProfile ? (
          <ResizablePanelGroup direction="horizontal" className="h-full">
            <ResizablePanel defaultSize={50} minSize={30}>
              <OAuthSequenceDiagram
                flowState={oauthFlowState}
                registrationStrategy={registrationStrategy}
                protocolVersion={protocolVersion}
                focusedStep={focusedStep}
                hasProfile={hasProfile}
                onConfigure={() => openProfileModal("edit")}
              />
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel defaultSize={50} minSize={20} maxSize={50}>
              <OAuthFlowLogger
                oauthFlowState={oauthFlowState}
                onClearLogs={clearInfoLogs}
                onClearHttpHistory={clearHttpHistory}
                activeStep={focusedStep ?? oauthFlowState.currentStep}
                onFocusStep={setFocusedStep}
                hasProfile={hasProfile}
                summary={{
                  label: hasProfile
                    ? serverIdentifier
                    : "No target configured",
                  description: headerDescription,
                  protocol: hasProfile ? protocolVersion : undefined,
                  registration: hasProfile
                    ? describeRegistrationStrategy(registrationStrategy)
                    : undefined,
                  step: oauthFlowState.currentStep,
                  serverUrl: hasProfile ? profile.serverUrl : undefined,
                  scopes:
                    hasProfile && profile.scopes.trim()
                      ? profile.scopes.trim()
                      : undefined,
                  clientId:
                    hasProfile && profile.clientId.trim()
                      ? profile.clientId.trim()
                      : undefined,
                  customHeadersCount: hasProfile
                    ? profile.customHeaders.filter((h) => h.key.trim()).length
                    : undefined,
                }}
                actions={{
                  onConfigure: () => openProfileModal("edit"),
                  onAddServer: () => openProfileModal("add"),
                  onReset: hasProfile ? () => resetOAuthFlow() : undefined,
                  // Hide Continue button when showing Connect/Refresh buttons
                  onContinue:
                    canApplyTokens || continueDisabled
                      ? undefined
                      : handleAdvance,
                  continueLabel,
                  continueDisabled: Boolean(
                    canApplyTokens || continueDisabled
                  ),
                  resetDisabled:
                    !hasProfile || oauthFlowState.isInitiatingAuth,
                  onConnectServer:
                    canApplyTokens && !isServerConnected
                      ? handleConnectServer
                      : undefined,
                  onRefreshTokens:
                    canApplyTokens && isServerConnected
                      ? () => setIsRefreshTokensModalOpen(true)
                      : undefined,
                  isApplyingTokens,
                }}
              />
            </ResizablePanel>
          </ResizablePanelGroup>
        ) : (
          // Empty / unconfigured: keep progressive disclosure tight — just the
          // diagram with its centered "Configure OAuth Target" overlay. The
          // logs sidebar only earns its space once a target is configured.
          <OAuthSequenceDiagram
            flowState={oauthFlowState}
            registrationStrategy={registrationStrategy}
            protocolVersion={protocolVersion}
            focusedStep={focusedStep}
            hasProfile={false}
            showConfigurePrompt={areServersHydrated && !hasHeaderServers}
            onConfigure={() => openProfileModal("edit")}
          />
        )}
      </div>

      {oauthFlowState.authorizationUrl && (
        <OAuthAuthorizationModal
          open={isAuthModalOpen}
          onOpenChange={setIsAuthModalOpen}
          authorizationUrl={oauthFlowState.authorizationUrl}
        />
      )}

      <OAuthProfileModal
        open={isProfileModalOpen}
        onOpenChange={setIsProfileModalOpen}
        server={profileModalMode === "add" ? undefined : activeServer}
        existingServerNames={Object.keys(serverConfigs)}
        onSave={async ({ formData, profile: savedProfile }) => {
          // Await and check the result: a failed save must not close the modal,
          // reset the flow, or move the selection onto a server that was never
          // written. The hook already toasted the reason, so throw a generic
          // message for the modal's inline error.
          const saved = await onSaveServerConfig?.(formData, {
            oauthProfile: savedProfile,
            originalServerName:
              profileModalMode === "add" ? undefined : activeServer?.name,
          });
          if (saved === false) {
            throw new Error("Could not save the server. Please try again.");
          }
          setPendingServerSelection(formData.name);
          resetOAuthFlow(formData.url);
        }}
      />

      {activeServer && (
        <RefreshTokensConfirmModal
          open={isRefreshTokensModalOpen}
          onOpenChange={setIsRefreshTokensModalOpen}
          serverName={activeServer.name}
          onConfirm={handleRefreshTokensConfirm}
          isLoading={isApplyingTokens}
        />
      )}
    </div>
  );
};
